import {
  COVERAGE_LEVEL_PRECISION_FOR_COMPARISONS,
  RmaLgmTypeCodes,
  RmaLgmTypeCode,
  RmaLgmCommodityCodes,
  RmaLgmCommodityCode,
} from '@harvestiq/constants';
import { dayjs, Dayjs } from './dayjs';
import { Decimal } from '@harvestiq/utils';
import { RmaPracticeCodes } from '@harvestiq/constants/rma/PracticeCodes';

/**
 * Takes an optional date as a string, e.g. 2024-04-24, or Date object and returns the associated reinsurance year.
 * If date is not provided uses new Date()
 */
export const getReinsuranceYear = (
  date: string | Date | Dayjs = new Date()
) => {
  let year, monthIndex;

  if (typeof date === 'string') {
    const splitDate = date.split('-').map(Number);
    year = splitDate[0];
    // Months are 0 indexed in JS
    const calendarMonth = splitDate[1];
    monthIndex = calendarMonth - 1;
  }

  if (date instanceof Date) {
    year = date.getFullYear();
    monthIndex = date.getMonth();
  }

  if (dayjs.isDayjs(date)) {
    year = date.year();
    monthIndex = date.month();
  }

  if (monthIndex === undefined || isNaN(monthIndex) || !year) {
    throw new Error('Invalid date');
  }

  // Reinsurance years are from July 1 of previous year to June 30
  // E.g. 2024 reinsurance year runs from July 1, 2023 to June 30, 2024
  // NOTE: Months are 0 indexed in JS, so July is 6
  const reinsuranceYear = monthIndex >= 6 ? year + 1 : year;
  return reinsuranceYear;
};

/**
 * Takes an date as a string, e.g. 2024-04-24, or Date object and returns the associated reinsurance years.
 */
export function getReinsuranceYearsForReleaseDate(
  tgtReleaseDate: string | Date | Dayjs
): Array<number> {
  let year, monthIndex;

  if (typeof tgtReleaseDate === 'string') {
    const splitDate = tgtReleaseDate.split('-').map(Number);
    year = splitDate[0];
    // Months are 0 indexed in JS
    const calendarMonth = splitDate[1];
    monthIndex = calendarMonth - 1;
  }

  if (tgtReleaseDate instanceof Date) {
    year = tgtReleaseDate.getFullYear();
    monthIndex = tgtReleaseDate.getMonth();
  }

  if (dayjs.isDayjs(tgtReleaseDate)) {
    year = tgtReleaseDate.year();
    monthIndex = tgtReleaseDate.month();
  }

  if (monthIndex === undefined || isNaN(monthIndex) || !year) {
    throw new Error('Invalid date');
  }

  // if today is 8/1/2024, then:
  // it is technically the reinsurance year 2025
  // LRP will publish reinsurance year 2024 files from 7/1/2023 to 7/31/2024
  //   and LRP will publish reinsurance year 2025 files from 7/1/2024 to 7/31/2026
  // LGM will publish reinsurance year 2024 files from 7/1/2023 to 7/31/2024
  //   and LGM will publish reinsurance year 2025 files from 7/1/2024 to 7/31/2025

  const reinsuranceYears = [year];
  if (monthIndex > 6) {
    // August - December
    reinsuranceYears.push(year + 1);
  } else if (monthIndex === 6) {
    // July
    reinsuranceYears.push(year + 1);
    reinsuranceYears.push(year - 1);
  } else {
    // January - June
    reinsuranceYears.push(year - 1);
  }

  return reinsuranceYears;
}

/**
 * Returns true if the passed RMA Commodity Type Code is a swine commodity
 */
export const isSwineCommodity = (
  commodityCode: RmaLgmCommodityCode,
  typeCode: RmaLgmTypeCode
) => {
  const isSwineCommodityCode = commodityCode === RmaLgmCommodityCodes.SWINE;
  const isSwine =
    typeCode === RmaLgmTypeCodes.FARROW_TO_FINISH ||
    typeCode === RmaLgmTypeCodes.SEW_PIG_FINISHING ||
    typeCode === RmaLgmTypeCodes.FINISHING;
  return isSwineCommodityCode && isSwine;
};

export const isDairyCattleCommodity = (
  commodityCode: RmaLgmCommodityCode,
  typeCode: RmaLgmTypeCode
) => {
  const isDairyCommodityCode =
    commodityCode === RmaLgmCommodityCodes.DAIRY_CATTLE;
  const isNoTypeSpecified = typeCode === RmaLgmTypeCodes.NO_TYPE_SPECIFIED;
  return isDairyCommodityCode && isNoTypeSpecified;
};

/**
 * Returns true if the reinsurance year is before 2025 or swine and legacy LGM calculations should be used
 */
export const usePre2025LgmCalculations = ({
  salesEffectiveDate,
  commodityCode,
  typeCode,
}: {
  salesEffectiveDate: string | Date | Dayjs;
  commodityCode: RmaLgmCommodityCode;
  typeCode: RmaLgmTypeCode;
}) => {
  // Swine retains legacy calculation methods
  const isSwine = isSwineCommodity(commodityCode, typeCode);
  const reinsuranceYear = getReinsuranceYear(salesEffectiveDate);

  return isSwine || reinsuranceYear < 2025;
};

export const getReinsuranceYearOptions = () => {
  const reinsuranceYearOptions = [];
  const currentReinsuranceYear = getReinsuranceYear();

  // create options from 2019 until current reinsurance year
  for (let year = currentReinsuranceYear; year >= 2019; year -= 1) {
    reinsuranceYearOptions.push({ label: year, value: year });
  }
  return reinsuranceYearOptions;
};

export function isSameCoverageLevel(
  coverageLevelA: Decimal,
  coverageLevelB: Decimal
) {
  const coverageLevelARoundUp = coverageLevelA.toDP(
    COVERAGE_LEVEL_PRECISION_FOR_COMPARISONS,
    Decimal.ROUND_UP
  );
  const coverageLevelBRoundUp = coverageLevelB.toDP(
    COVERAGE_LEVEL_PRECISION_FOR_COMPARISONS,
    Decimal.ROUND_UP
  );
  const coverageLevelARoundDown = coverageLevelA.toDP(
    COVERAGE_LEVEL_PRECISION_FOR_COMPARISONS,
    Decimal.ROUND_DOWN
  );
  const coverageLevelBRoundDown = coverageLevelB.toDP(
    COVERAGE_LEVEL_PRECISION_FOR_COMPARISONS,
    Decimal.ROUND_DOWN
  );
  const coverageLevelAToPrecision = coverageLevelA.toPrecision(
    COVERAGE_LEVEL_PRECISION_FOR_COMPARISONS
  );
  const coverageLevelBToPrecision = coverageLevelB.toPrecision(
    COVERAGE_LEVEL_PRECISION_FOR_COMPARISONS
  );

  const isEqual =
    coverageLevelA.eq(coverageLevelB) ||
    coverageLevelARoundUp.eq(coverageLevelBRoundUp) ||
    coverageLevelARoundUp.eq(coverageLevelBRoundDown) ||
    coverageLevelARoundDown.eq(coverageLevelBRoundUp) ||
    coverageLevelARoundDown.eq(coverageLevelBRoundDown) ||
    coverageLevelAToPrecision === coverageLevelBToPrecision;

  return isEqual;
}

type GetRmaLrpPracticeCodeArgs = {
  salesEffectiveDate: string;
  endDate: string;
};

export function getRmaLrpPracticeCode({
  salesEffectiveDate,
  endDate,
}: GetRmaLrpPracticeCodeArgs) {
  if (!dayjs(salesEffectiveDate).isValid()) {
    throw new Error(`Invalid sales effective date: ${salesEffectiveDate}`);
  }

  if (!dayjs(endDate).isValid()) {
    throw new Error(`Invalid end date: ${endDate}`);
  }

  const endDateMonth = dayjs(endDate).month();

  const salesEffecticeDateReinsuranceYear =
    getReinsuranceYear(salesEffectiveDate);
  const endDateReinsuranceYear = getReinsuranceYear(endDate);
  const isSameReinsuranceYear =
    salesEffecticeDateReinsuranceYear === endDateReinsuranceYear;

  if (isSameReinsuranceYear) {
    if (endDateMonth === 0) {
      return RmaPracticeCodes.EndorsementEndingInJanuaryYr1;
    } else if (endDateMonth === 1) {
      return RmaPracticeCodes.EndorsementEndingInFebruaryYr1;
    } else if (endDateMonth === 2) {
      return RmaPracticeCodes.EndorsementEndingInMarchYr1;
    } else if (endDateMonth === 3) {
      return RmaPracticeCodes.EndorsementEndingInAprilYr1;
    } else if (endDateMonth === 4) {
      return RmaPracticeCodes.EndorsementEndingInMayYr1;
    } else if (endDateMonth === 5) {
      return RmaPracticeCodes.EndorsementEndingInJuneYr1;
    } else if (endDateMonth === 6) {
      return RmaPracticeCodes.EndorsementEndingInJulyYr1;
    } else if (endDateMonth === 7) {
      return RmaPracticeCodes.EndorsementEndingInAugustYr1;
    } else if (endDateMonth === 8) {
      return RmaPracticeCodes.EndorsementEndingInSeptemberYr1;
    } else if (endDateMonth === 9) {
      return RmaPracticeCodes.EndorsementEndingInOctoberYr1;
    } else if (endDateMonth === 10) {
      return RmaPracticeCodes.EndorsementEndingInNovemberYr1;
    } else if (endDateMonth === 11) {
      return RmaPracticeCodes.EndorsementEndingInDecemberYr1;
    } else {
      throw new Error(`Unsupported month for RMA LRP: ${endDateMonth}`);
    }
  } else {
    if (endDateMonth === 0) {
      return RmaPracticeCodes.EndorsementEndingInJanuaryYr2;
    } else if (endDateMonth === 1) {
      return RmaPracticeCodes.EndorsementEndingInFebruaryYr2;
    } else if (endDateMonth === 2) {
      return RmaPracticeCodes.EndorsementEndingInMarchYr2;
    } else if (endDateMonth === 3) {
      return RmaPracticeCodes.EndorsementEndingInAprilYr2;
    } else if (endDateMonth === 4) {
      return RmaPracticeCodes.EndorsementEndingInMayYr2;
    } else if (endDateMonth === 5) {
      return RmaPracticeCodes.EndorsementEndingInJuneYr2;
    } else if (endDateMonth === 6) {
      return RmaPracticeCodes.EndorsementEndingInJulyYr2;
    } else if (endDateMonth === 7) {
      return RmaPracticeCodes.EndorsementEndingInAugustYr2;
    } else if (endDateMonth === 8) {
      return RmaPracticeCodes.EndorsementEndingInSeptemberYr2;
    } else if (endDateMonth === 9) {
      return RmaPracticeCodes.EndorsementEndingInOctoberYr2;
    } else if (endDateMonth === 10) {
      return RmaPracticeCodes.EndorsementEndingInNovemberYr2;
    } else if (endDateMonth === 11) {
      return RmaPracticeCodes.EndorsementEndingInDecemberYr2;
    } else {
      throw new Error(`Unsupported month for RMA LRP: ${endDateMonth}`);
    }
  }
}

type GetRmaLgmPracticeCodeArgs = {
  commodityCode: RmaLgmCommodityCode;
  salesEffectiveDate: string;
};

export function getRmaLgmPracticeCode({
  commodityCode,
  salesEffectiveDate,
}: GetRmaLgmPracticeCodeArgs) {
  if (!dayjs(salesEffectiveDate).isValid()) {
    throw new Error(`Invalid sales effective date: ${salesEffectiveDate}`);
  }

  const month = dayjs(salesEffectiveDate).get('month');

  switch (commodityCode) {
    case RmaLgmCommodityCodes.CATTLE:
    case RmaLgmCommodityCodes.DAIRY_CATTLE: {
      if (month === 0) {
        return RmaPracticeCodes.JanNovInsurancePeriod;
      } else if (month === 1) {
        return RmaPracticeCodes.FebDecInsurancePeriod;
      } else if (month === 2) {
        return RmaPracticeCodes.MarJanInsurancePeriod;
      } else if (month === 3) {
        return RmaPracticeCodes.AprFebInsurancePeriod;
      } else if (month === 4) {
        return RmaPracticeCodes.MayMarInsurancePeriod;
      } else if (month === 5) {
        return RmaPracticeCodes.JuneAprInsurancePeriod;
      } else if (month === 6) {
        return RmaPracticeCodes.JulyMayInsurancePeriod;
      } else if (month === 7) {
        return RmaPracticeCodes.AugJunInsurancePeriod;
      } else if (month === 8) {
        return RmaPracticeCodes.SepJulInsurancePeriod;
      } else if (month === 9) {
        return RmaPracticeCodes.OctAugInsurancePeriod;
      } else if (month === 10) {
        return RmaPracticeCodes.NovSepInsurancePeriod;
      } else if (month === 11) {
        return RmaPracticeCodes.DecOctInsurancePeriod;
      } else {
        throw new Error(
          `Unsupported month for RMA LGM ${RmaLgmCommodityCodes.CATTLE}: ${month}`
        );
      }
    }

    case RmaLgmCommodityCodes.SWINE: {
      if (month === 0) {
        return RmaPracticeCodes.JanJuneInsurancePeriod;
      } else if (month === 1) {
        return RmaPracticeCodes.FebJulyInsurancePeriod;
      } else if (month === 2) {
        return RmaPracticeCodes.MarAugInsurancePeriod;
      } else if (month === 3) {
        return RmaPracticeCodes.AprSepInsurancePeriod;
      } else if (month === 4) {
        return RmaPracticeCodes.MayOctInsurancePeriod;
      } else if (month === 5) {
        return RmaPracticeCodes.JuneNovInsurancePeriod;
      } else if (month === 6) {
        return RmaPracticeCodes.JulyDecInsurancePeriod;
      } else if (month === 7) {
        return RmaPracticeCodes.AugJanInsurancePeriod;
      } else if (month === 8) {
        return RmaPracticeCodes.SepFebInsurancePeriod;
      } else if (month === 9) {
        return RmaPracticeCodes.OctMarInsurancePeriod;
      } else if (month === 10) {
        return RmaPracticeCodes.NovAprInsurancePeriod;
      } else if (month === 11) {
        return RmaPracticeCodes.DecMayInsurancePeriod;
      } else {
        throw new Error(
          `Unsupported month for RMA LGM ${RmaLgmCommodityCodes.SWINE}: ${month}`
        );
      }
    }

    default: {
      throw new Error(`Unsupported RMA LGM commodity code: ${commodityCode}`);
    }
  }
}
